import type { EnhancedDomain } from 'jms-domain'
import { ApplicationTypes, Model, StackSystem, createRecipe, getBottomWallStackId, round } from 'jms-domain'
import { CabinetWidth } from 'jms-domain/src/implementations/common'
import { match } from 'ts-pattern'

import type { DomainState, IdSequence, StateUpdate } from '@salsita-ai/platform-project-sdk/domain'

import { configuratorInterface } from '../systems/configurator-interface.js'

interface WallContentItem {
  width: CabinetWidth
  stackItemRecipe: Model.AnyDomainEntity
  otherRecipes: Model.Appliance[]
}

const createDrawerDoorCabinet = (
  wallId: Model.Wall['id'],
  sequence: IdSequence<Model.AnyDomainEntity>,
  sideGap: number
) => {
  return {
    width: round(CabinetWidth.W24 + sideGap, 4),
    stackItemRecipe: createRecipe({
      id: sequence.nextId('Drawer33Door66'),
      type: 'Drawer33Door66',
      handing: Model.Handing.left,
      wallId,
      width: CabinetWidth.W24,
    }),
    otherRecipes: [],
  }
}

const createFullDoorCabinet = (
  wallId: Model.Wall['id'],
  sequence: IdSequence<Model.AnyDomainEntity>,
  width: Model.FullDoorBase['width'],
  sideGap: number
) => {
  return {
    width: round(width + sideGap, 4),
    stackItemRecipe: createRecipe({
      id: sequence.nextId('FullDoorBase'),
      type: 'FullDoorBase',
      handing: Model.Handing.left,
      wallId,
      width,
    }),
    otherRecipes: [],
  }
}

const createFiller = (
  wallId: Model.Wall['id'],
  sequence: IdSequence<Model.AnyDomainEntity>,
  width: Model.Filler['width'],
  sideGap: number
) => {
  return {
    width: round(width + sideGap, 4),
    stackItemRecipe: createRecipe({
      id: sequence.nextId('Filler'),
      type: 'Filler',
      wallId,
      width,
    }),
    otherRecipes: [],
  }
}

const createEndPanel = (wallId: Model.Wall['id'], sequence: IdSequence<Model.AnyDomainEntity>) => {
  return {
    width: CabinetWidth.W1,
    stackItemRecipe: createRecipe({
      id: sequence.nextId('EndPanel'),
      type: 'EndPanel',
      wallId,
    }),
    otherRecipes: [],
  }
}

const getWallWidth = ($wall: EnhancedDomain.$Wall) => {
  return round(Math.abs($wall.$stackFillFrom - $wall.$stackFillTo), 4)
}

/**
 * When user switches room shape, fill the room with cabinets.
 */
export const fillRoomTransformer = configuratorInterface.transformerBuilder.when
  .navigated({ to: [ApplicationTypes.NavigationState.RoomContent] })
  .and(ctx => ctx.getOld$Root().$items.length === 0)
  .then.applySideEffect(ctx => {
    const $room = ctx.getNew$Root()
    const stackSystem = new StackSystem({
      recipeCooker: ctx.domainContext.recipeCooker,
    })

    // TODO: This state reduction is a hack because we are missing an ability to `prepareCreateUpdates` for several parents at once!
    let updatedState: DomainState<Model.AnyDomainEntity> = ctx.getNewState()

    const sequence = ctx.getIdSequence()
    const pendingUpdates: Array<StateUpdate<Model.AnyDomainEntity, Model.AnyDomainEntity>> = []

    for (const { id: wallId } of $room.$walls) {
      const $updatedRoom = ctx.domainContext.domain.getRoot$Entity(updatedState)
      const $wall = $updatedRoom.$walls.find($w => $w.id === wallId)
      if (!$wall) {
        continue
      }

      const sideGap = $room.$cabinetSideGap

      // Pregenerate items for fixes sized wall.
      // There are no items generated for walls which should be fully occupied,
      // these are handled dynamically by the `fillStack` function below.
      const items = match($room.shape)
        .with(Model.RoomShapeType.I, () => [
          createEndPanel($wall.id, sequence),
          createFullDoorCabinet($wall.id, sequence, CabinetWidth.W24, sideGap),
          createEndPanel($wall.id, sequence),
        ])
        .with(Model.RoomShapeType.L, () =>
          match($wall.wallType)
            .with(Model.WallType.A, () => [
              createFullDoorCabinet($wall.id, sequence, CabinetWidth.W24, sideGap),
              createEndPanel($wall.id, sequence),
            ])
            .with(Model.WallType.B, () => [
              createFullDoorCabinet($wall.id, sequence, CabinetWidth.W24, sideGap),
              createEndPanel($wall.id, sequence),
            ])
            .otherwise(() => null)
        )
        .with(Model.RoomShapeType.U, () =>
          match($wall.wallType)
            .with(Model.WallType.A, () => [
              createFullDoorCabinet($wall.id, sequence, CabinetWidth.W24, sideGap),
              createEndPanel($wall.id, sequence),
            ])
            .with(Model.WallType.C, () => [
              createFullDoorCabinet($wall.id, sequence, CabinetWidth.W24, sideGap),
              createEndPanel($wall.id, sequence),
            ])
            .otherwise(() => null)
        )
        .otherwise(() => null)

      const stackUpdates = stackSystem.fillStack(
        {
          from: $wall.$stackFillFrom,
          to: $wall.$stackFillTo,
          parentEntityId: $wall.id,
          stackKey: getBottomWallStackId($wall.id),
          stackItemInstance: 'stackItem',
        },
        x => {
          const relX = Math.floor($wall.$stackFillFrom > $wall.$stackFillTo ? $wall.$stackFillFrom - x : x)

          if (items) {
            const item = items.shift()

            if (item) {
              return item
            } else {
              // Make last item too big to fit.
              return {
                ...createDrawerDoorCabinet($wall.id, sequence, sideGap),
                width: 100000000,
              }
            }
          } else {
            const wallLength = getWallWidth($wall)
            const spaceLeft = round(wallLength - relX, 2)

            let item: WallContentItem

            if (spaceLeft >= CabinetWidth.W36) {
              item = createFullDoorCabinet($wall.id, sequence, CabinetWidth.W24, sideGap)
            } else if (spaceLeft >= CabinetWidth.W30) {
              item = createFullDoorCabinet($wall.id, sequence, CabinetWidth.W18, sideGap)
            } else if (spaceLeft >= CabinetWidth.W24) {
              item = createFullDoorCabinet($wall.id, sequence, CabinetWidth.W24, sideGap)
            } else if (spaceLeft >= CabinetWidth.W18) {
              item = createFullDoorCabinet($wall.id, sequence, CabinetWidth.W18, sideGap)
            } else {
              item = createFullDoorCabinet($wall.id, sequence, CabinetWidth.W12, sideGap)
            }

            // Item fits, there's less than 1/4" left.
            if (spaceLeft - item.width < $room.$ignoredCabinetSpace) {
              return item
            }

            // When there's more space then to fill it with a filler, we return the cabinet.
            const spaceLeftAfterItem = round(spaceLeft - item.width, 2)
            if (spaceLeftAfterItem > CabinetWidth.W5) {
              return item
            }

            // Otherwise return the filler first before the last cabiner.
            // Filler can't be connected to a corner filler.
            if (spaceLeftAfterItem >= CabinetWidth.W5) {
              return createFiller($wall.id, sequence, CabinetWidth.W5, sideGap)
            } else if (spaceLeftAfterItem >= CabinetWidth.W4_75) {
              return createFiller($wall.id, sequence, CabinetWidth.W4_75, sideGap)
            } else if (spaceLeftAfterItem >= CabinetWidth.W4_5) {
              return createFiller($wall.id, sequence, CabinetWidth.W4_5, sideGap)
            } else if (spaceLeftAfterItem >= CabinetWidth.W4_25) {
              return createFiller($wall.id, sequence, CabinetWidth.W4_25, sideGap)
            } else if (spaceLeftAfterItem >= CabinetWidth.W4) {
              return createFiller($wall.id, sequence, CabinetWidth.W4, sideGap)
            } else if (spaceLeftAfterItem >= CabinetWidth.W3_75) {
              return createFiller($wall.id, sequence, CabinetWidth.W3_75, sideGap)
            } else if (spaceLeftAfterItem >= CabinetWidth.W3_5) {
              return createFiller($wall.id, sequence, CabinetWidth.W3_5, sideGap)
            } else if (spaceLeftAfterItem >= CabinetWidth.W3_25) {
              return createFiller($wall.id, sequence, CabinetWidth.W3_25, sideGap)
            } else if (spaceLeftAfterItem >= CabinetWidth.W3) {
              return createFiller($wall.id, sequence, CabinetWidth.W3, sideGap)
            } else if (spaceLeftAfterItem >= CabinetWidth.W2_75) {
              return createFiller($wall.id, sequence, CabinetWidth.W2_75, sideGap)
            } else if (spaceLeftAfterItem >= CabinetWidth.W2_5) {
              return createFiller($wall.id, sequence, CabinetWidth.W2_5, sideGap)
            } else if (spaceLeftAfterItem >= CabinetWidth.W2_25) {
              return createFiller($wall.id, sequence, CabinetWidth.W2_25, sideGap)
            } else if (spaceLeftAfterItem >= CabinetWidth.W2) {
              return createFiller($wall.id, sequence, CabinetWidth.W2, sideGap)
            } else if (spaceLeftAfterItem >= CabinetWidth.W1_75) {
              return createFiller($wall.id, sequence, CabinetWidth.W1_75, sideGap)
            } else if (spaceLeftAfterItem >= CabinetWidth.W1_5) {
              return createFiller($wall.id, sequence, CabinetWidth.W1_5, sideGap)
            } else if (spaceLeftAfterItem >= CabinetWidth.W1_25) {
              return createFiller($wall.id, sequence, CabinetWidth.W1_25, sideGap)
            } else {
              return createFiller($wall.id, sequence, CabinetWidth.W1, sideGap)
            }
          }
        },
        updatedState
      )

      updatedState = ctx.domainContext.storeSystem.getNewState(stackUpdates, updatedState)

      pendingUpdates.push(...stackUpdates)
    }

    return pendingUpdates
  })
  .build('Fill room with cabinets')
